import React, { useEffect, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import { supabase } from "../supabaseClient";
import "mapbox-gl/dist/mapbox-gl.css";
import { Box, IconButton, Chip } from "@mui/material";
import MapIcon from '@mui/icons-material/Map';
import SearchIcon from '@mui/icons-material/Search';
import PlaceViewDialog from "../components/PlaceViewDialog";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const initialCenter = [126.9780, 37.5665]; // 서울 시청

const MapView = ({ userId }) => {
  const mapRef = useRef(null);
  const mapContainer = useRef(null);
  const [categories, setCategories] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const markersRef = useRef([]);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("places_category")
        .select("id, title, icon, color");
      if (error) {
        console.error("카테고리 가져오기 실패:", error.message);
        return;
      }
      setCategories(data);
    };
    fetchCategories();
  }, []);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
  }, []);

  const fetchNearby = useCallback(async (lat, lng, map) => {
    clearMarkers();
    const { data, error } = await supabase.rpc("get_places_nearby", {
      in_lat: lat,
      in_lng: lng,
      in_radius: 10000,
    });

    if (error) {
      console.error("장소 가져오기 오류:", error);
      return;
    }

    if (data && data.length > 0) {
      data.forEach((place) => {
        const geo = place.location_geojson;
        
        const el = document.createElement("div");
        el.style.display = "flex";
        el.style.flexDirection = "column";
        el.style.alignItems = "center";
        
        const color = place.color;
        const markerDot = document.createElement("div");
        markerDot.style.width = "12px";
        markerDot.style.height = "12px";
        markerDot.style.borderRadius = "50%";
        markerDot.style.backgroundColor = color;
        markerDot.style.border = "2px solid white";
        markerDot.style.boxShadow = "0 0 3px rgba(0,0,0,0.3)";

        const label = document.createElement("div");
        label.innerText = place.title || "장소";
        label.style.fontSize = "11px";
        label.style.marginTop = "4px";
        label.style.padding = "2px 6px";
        label.style.background = "#ffffffCC";
        label.style.borderRadius = "4px";
        label.style.boxShadow = "0 1px 2px rgba(0,0,0,0.2)";
        label.style.whiteSpace = "nowrap";

        el.appendChild(markerDot);
        el.appendChild(label);

        const newMarker = new mapboxgl.Marker(el)
          .setLngLat(geo.coordinates)
          .addTo(map);

        newMarker.getElement().addEventListener("click", (e) => {
          e.preventDefault();
          setTimeout(() => {
            setSelectedPlaceId(place.place_id);
            setDialogOpen(true);
          }, 10);
        });

        markersRef.current.push(newMarker);
      });
    }
  }, [clearMarkers]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation({ lat: latitude, lng: longitude });
      },
      () => {
        setUserLocation({ lat: initialCenter[1], lng: initialCenter[0] });
      }
    );
  }, []);

  useEffect(() => {
    if (!userLocation) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [userLocation.lng, userLocation.lat],
      zoom: 14,
    });

    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), "top-right");
    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      }),
      "top-right"
    );

    new mapboxgl.Marker({ color: "blue" })
      .setLngLat([userLocation.lng, userLocation.lat])
      .setPopup(new mapboxgl.Popup().setText("내 위치"))
      .addTo(map);

    fetchNearby(userLocation.lat, userLocation.lng, map);

    return () => {
      map.remove();
    };
  }, [userLocation, fetchNearby]);

  const handleResetView = () => {
    if (mapRef.current) {
      mapRef.current.flyTo({ center: initialCenter, zoom: 10 });
    }
  };

  const handleSearchHere = () => {
    if (!mapRef.current) return;
    const center = mapRef.current.getCenter();
    fetchNearby(center.lat, center.lng, mapRef.current);
  };

  return (
    <Box style={{ width: "100%", height: "100vh" }}>
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }}></div>

      <Box sx={{ 
          position : "absolute",
          left: 5,
          right: 5,
          top : 60,
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
          gap: 0.5,
          mt: 1,
          "&::-webkit-scrollbar": {
              height: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: "4px",
          },
        }}>
        {categories.map((cat) => (
          <Chip label={cat.title} key={cat.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, flexDirection:"row" }}></Chip>
        ))}
      </Box>

      <IconButton
        onClick={handleResetView}
        sx={{
          position: "absolute",
          bottom: 80,
          right: 16,
          backgroundColor: "white",
          boxShadow: 2,
          "&:hover": { backgroundColor: "#f0f0f0" },
        }}
      >
        <MapIcon color="primary" />
      </IconButton>

      <IconButton
        onClick={handleSearchHere}
        sx={{
          position: "absolute",
          bottom: 140,
          right: 16,
          backgroundColor: "white",
          boxShadow: 2,
          "&:hover": { backgroundColor: "#f0f0f0" },
        }}
      >
        <SearchIcon color="primary" />
      </IconButton>

      <PlaceViewDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        placeId={selectedPlaceId}
      />
    </Box>
  );
};

export default MapView;
